import { useMemo } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { useSessionQuery } from "./useSessionQuery";
import { useStoreQuery } from "./useStoreQuery";
import { useOrderStatusCountsQuery } from "@/modules/orders/services/useOrderStatusCountsQuery";
import type { DashboardTask } from "@/modules/dashboard/components/DashboardSidebarCards";

export type SellerTasks = {
  /** Card heading — "Complete setup" while onboarding, "Today's tasks" after. */
  title: string;
  tasks: DashboardTask[];
};

/**
 * Drives the "Today's tasks" card. The store must finish onboarding first: while
 * any setup step (categories / products / payment options) is missing, the card
 * shows those steps as clickable tasks that deep-link to where they're
 * completed. Once setup is done it flips to the real daily task list. Both are
 * sourced from data the app shell already has (session overview + order counts),
 * so it adds no extra network cost.
 */
export function useSellerTasks({
  storeSlug,
  locale,
}: {
  storeSlug: string;
  locale: string;
}): SellerTasks {
  const t = useTranslations();
  const { data: sessionData } = useSessionQuery({ storeSlug });
  const { data: storeData } = useStoreQuery({ storeSlug });
  const { data: counts } = useOrderStatusCountsQuery({
    storeSlug,
    refetchInterval: 2 * 60 * 1000,
  });

  const overview = sessionData?.overview ?? null;
  const seller = sessionData?.seller ?? null;

  const hasCategories = overview?.hasCategories === true;
  const hasProducts = overview?.hasProducts === true;
  // A store can get paid via an active online method OR cash on delivery, so
  // COD counts as a configured payment method.
  const hasPayments =
    overview?.hasActivePaymentOptions === true || storeData?.acceptsCod === true;
  const setupComplete = hasCategories && hasProducts && hasPayments;

  const actionable =
    (counts?.counts.pending_payment_verification ?? 0) +
    (counts?.counts.paid_less_than_required ?? 0);

  // Titles resolved outside the memo — `t` is a fresh closure each render.
  const setupTitle = t(keys.seller.sidebarSetupTitle);
  const tasksTitle = t(keys.dashboard.tasksTitle);
  const setupCategoriesTitle = t(keys.seller.setupAddCategories);
  const setupProductTitle = t(keys.seller.setupAddProduct);
  const setupPaymentTitle = t(keys.seller.setupPaymentOptions);
  const fulfillTitle =
    actionable > 0
      ? t(
          actionable === 1
            ? keys.dashboard.taskFulfillPendingOne
            : keys.dashboard.taskFulfillPendingMany,
          { count: actionable },
        )
      : t(keys.dashboard.taskOrdersOnTrack);
  const profileTitle = t(keys.seller.setupCompleteProfile);

  // Deep links for each setup step.
  const categoriesHref = getSellerPath(locale, storeSlug, "/categories");
  const productsHref = getSellerPath(locale, storeSlug, "/products");
  const paymentHref = `${getSellerPath(locale, storeSlug, "/settings")}?tab=payment&section=payment`;

  return useMemo<SellerTasks>(() => {
    if (!setupComplete) {
      // Show every setup step with its real state — a finished step stays
      // visible but checked; an unfinished one (e.g. payment not configured)
      // stays unchecked so the state is never misleading. The overview flags
      // are the source of truth and refresh as soon as a step is completed.
      return {
        title: setupTitle,
        tasks: [
          { done: hasCategories, title: setupCategoriesTitle, href: categoriesHref },
          { done: hasProducts, title: setupProductTitle, href: productsHref },
          { done: hasPayments, title: setupPaymentTitle, href: paymentHref },
        ],
      };
    }
    return {
      title: tasksTitle,
      tasks: [
        { done: false, title: fulfillTitle, urgent: actionable > 0 },
        { done: hasProducts, title: setupProductTitle },
        { done: hasPayments, title: setupPaymentTitle },
        { done: (seller?.name?.trim().length ?? 0) > 0, title: profileTitle },
      ],
    };
  }, [
    setupComplete,
    setupTitle,
    tasksTitle,
    hasCategories,
    hasProducts,
    hasPayments,
    setupCategoriesTitle,
    setupProductTitle,
    setupPaymentTitle,
    categoriesHref,
    productsHref,
    paymentHref,
    fulfillTitle,
    actionable,
    seller?.name,
    profileTitle,
  ]);
}
